/**
 * Self Assessment "UK property" pages (SA105): what to enter in each box of the return,
 * for one individual owner and tax year, with notes, drill-down to the transactions
 * behind each figure, the property allowance comparison, printing and CSV export.
 * Record-keeping output only: not tax advice.
 */
import { NOT_TAX_ADVICE } from "@shared/constants.ts";
import { taxYearLabel } from "@shared/periods.ts";
import { v } from "@shared/validation.ts";
import { Fragment, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Row, Table } from "react-bootstrap";
import { Link } from "react-router";
import { CheckboxField, DateField, MoneyField, TextField } from "../../components/forms/fields.tsx";
import { ModalFormButton } from "../../components/forms/ModalForm.tsx";
import { Icon } from "../../components/ui/Icon.tsx";
import {
  Can,
  EmptyState,
  Loading,
  Money,
  PageHeader,
  QueryState,
  Section,
} from "../../components/ui/kit.tsx";
import { api, withQuery } from "../../lib/api.ts";
import { useFormat } from "../../lib/format.ts";
import { useLookups } from "../../lib/lookups.ts";
import { useApi } from "../../lib/query.ts";
import { useUrlState } from "../../lib/url-state.ts";

interface Box {
  box: string;
  label: string;
  kind: "amount" | "count" | "tick" | "not_calculated";
  value: number | null;
  note: string;
  entryIds: string[];
}

interface Sa105Report {
  owner: { id: string; name: string; consolidatedExpenses: boolean };
  year: number;
  label: string;
  range: { from: string; to: string };
  basis: "cash" | "accruals";
  inputs: {
    lossBroughtForwardPence: number;
    financeCostsBroughtForwardPence: number;
    claimPropertyAllowance: boolean;
    updatedAt: string | null;
  };
  boxes: Box[];
  workingSheet: { incomePence: number; deductionsPence: number; adjustedPence: number };
  allowanceComparison: {
    turnoverPence: number;
    expensesRoute: { taxableProfitPence: number; financeCostReductionPence: number };
    allowanceRoute: { allowancePence: number; taxableProfitPence: number };
    lowerTaxableProfit: "expenses" | "allowance" | "same";
  };
  financeCostReduction: {
    costsPence: number;
    reliefBasePence: number;
    estimatedReductionPence: number;
    carriedForwardPence: number;
  };
  excluded: { capitalPence: number; nonAllowablePence: number; nonPropertyReceiptsPence: number };
  warnings: string[];
  jointlyLetProperties: Array<{ id: string; label: string }>;
  submission: {
    submittedAt: string;
    submittedOnDate: string | null;
    hmrcReceiptId: string | null;
    submittedByName: string | null;
    changedBoxes: string[];
  } | null;
  changedSinceSubmission: boolean;
  filingDeadline: string;
  generatedAt: string;
}

interface BoxEntries {
  box: string;
  totalPence: number;
  entries: Array<{
    id: string;
    source: "income" | "expense" | "loan_transaction";
    date: string;
    propertyLabel: string;
    typeLabel: string;
    supplierName: string | null;
    description: string;
    amountPence: number;
  }>;
}

const SECTIONS: Array<{ title: string; boxes: string[] }> = [
  { title: "UK property details", boxes: ["1", "3"] },
  { title: "Property income", boxes: ["20", "20.1", "20.2", "22", "23"] },
  { title: "Property expenses", boxes: ["24", "25", "26", "27", "28", "29"] },
  {
    title: "Calculating your taxable profit or loss",
    boxes: ["30", "31", "32–35", "36", "37", "38", "39", "40", "41", "42", "43", "44", "45"],
  },
];

const inputsSchema = v.object({
  lossBroughtForwardPence: v.pence({ min: 0, fromPounds: true }).default(0),
  financeCostsBroughtForwardPence: v.pence({ min: 0, fromPounds: true }).default(0),
  claimPropertyAllowance: v.boolean(),
});

const filedSchema = v.object({
  submittedOn: v.date(),
  hmrcReceiptId: v.string({ max: 100 }).optional(),
  notes: v.string({ max: 2000 }).default(""),
});

function sourceLink(e: BoxEntries["entries"][number]): string | null {
  if (e.source === "income") return `/income/${e.id}`;
  if (e.source === "expense") return `/expenses/${e.id}`;
  return null;
}

function Drilldown({ ownerId, year, box }: { ownerId: string; year: number; box: string }) {
  const f = useFormat();
  const q = useApi<BoxEntries>("/api/tax/sa105/entries", { ownerId, year, box });
  if (q.isLoading) return <Loading />;
  return (
    <QueryState query={q}>
      {(d) =>
        d.entries.length === 0 ? (
          <p className="small text-muted mb-0">No transactions.</p>
        ) : (
          <Table size="sm" className="mb-0 small">
            <thead>
              <tr>
                <th>Date</th>
                <th>Property</th>
                <th>Category</th>
                <th>Details</th>
                <th className="text-end">This owner's share</th>
              </tr>
            </thead>
            <tbody>
              {d.entries.map((e) => {
                const link = sourceLink(e);
                return (
                  <tr key={e.id}>
                    <td className="text-nowrap">
                      {link ? <Link to={link}>{f.date(e.date)}</Link> : f.date(e.date)}
                    </td>
                    <td>{e.propertyLabel}</td>
                    <td>{e.typeLabel}</td>
                    <td>{[e.supplierName, e.description].filter(Boolean).join(" · ")}</td>
                    <td className="text-end">
                      <Money pence={e.amountPence} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="fw-semibold">
                <td colSpan={4}>Total</td>
                <td className="text-end">
                  <Money pence={d.totalPence} />
                </td>
              </tr>
            </tfoot>
          </Table>
        )
      }
    </QueryState>
  );
}

function BoxValue({ box }: { box: Box }) {
  switch (box.kind) {
    case "tick":
      return box.value ? <strong>X</strong> : <span className="text-muted">blank</span>;
    case "count":
      return <strong>{box.value}</strong>;
    case "not_calculated":
      return <span className="text-muted small">not calculated</span>;
    default:
      return <Money pence={box.value ?? 0} />;
  }
}

function BoxTable({ report, changed }: { report: Sa105Report; changed: Set<string> }) {
  const [open, setOpen] = useState<string | null>(null);
  const byId = new Map(report.boxes.map((b) => [b.box, b]));
  return (
    <Table className="mb-0 align-middle sa105-boxes">
      <thead>
        <tr>
          <th style={{ width: "5rem" }}>Box</th>
          <th>Description and notes</th>
          <th className="text-end" style={{ width: "10rem" }}>
            Enter
          </th>
        </tr>
      </thead>
      <tbody>
        {SECTIONS.map((section) => (
          <Fragment key={section.title}>
            <tr className="table-light">
              <th colSpan={3}>{section.title}</th>
            </tr>
            {section.boxes.map((id) => {
              const b = byId.get(id);
              if (!b) return null;
              const drillable = b.entryIds.length > 0;
              return (
                <Fragment key={id}>
                  <tr className={b.kind === "not_calculated" ? "text-muted" : undefined}>
                    <td className="fw-semibold">{b.box}</td>
                    <td>
                      {b.label}
                      {changed.has(b.box) && (
                        <Badge bg="warning" text="dark" className="ms-2">
                          changed since filed
                        </Badge>
                      )}
                      <div className="small text-muted">{b.note}</div>
                    </td>
                    <td className="text-end text-nowrap">
                      {drillable ? (
                        <Button
                          variant="link"
                          className="p-0"
                          aria-expanded={open === id}
                          onClick={() => setOpen(open === id ? null : id)}
                        >
                          <BoxValue box={b} />
                        </Button>
                      ) : (
                        <BoxValue box={b} />
                      )}
                    </td>
                  </tr>
                  {open === id && (
                    <tr className="no-print">
                      <td />
                      <td colSpan={2} className="bg-body-tertiary">
                        <Drilldown ownerId={report.owner.id} year={report.year} box={id} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </Fragment>
        ))}
      </tbody>
    </Table>
  );
}

function Comparison({ r }: { r: Sa105Report }) {
  const c = r.allowanceComparison;
  return (
    <Section title="Deduct expenses, or claim the £1,000 property income allowance?">
      <Table size="sm" className="mb-2">
        <thead>
          <tr>
            <th />
            <th className="text-end">Deduct expenses</th>
            <th className="text-end">Claim the allowance</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Property income (boxes 20 + 22 + 23)</td>
            <td className="text-end">
              <Money pence={c.turnoverPence} />
            </td>
            <td className="text-end">
              <Money pence={c.turnoverPence} />
            </td>
          </tr>
          <tr>
            <td>Taxable profit</td>
            <td className={`text-end ${c.lowerTaxableProfit === "expenses" ? "fw-bold" : ""}`}>
              <Money pence={c.expensesRoute.taxableProfitPence} />
            </td>
            <td className={`text-end ${c.lowerTaxableProfit === "allowance" ? "fw-bold" : ""}`}>
              <Money pence={c.allowanceRoute.taxableProfitPence} />
            </td>
          </tr>
          <tr>
            <td>Residential finance cost reduction (20%, estimate)</td>
            <td className="text-end">
              <Money pence={c.expensesRoute.financeCostReductionPence} />
            </td>
            <td className="text-end text-muted">not available</td>
          </tr>
        </tbody>
      </Table>
      <p className="small text-muted mb-0">
        The allowance replaces all expenses and the finance cost reduction, and can't create a loss. You can't
        claim it on income from a connected party (such as a company you control). Which is better depends on
        your other income and tax rate, so check with an adviser if you're unsure.{" "}
        {r.inputs.claimPropertyAllowance
          ? "You're currently claiming the allowance."
          : "You're currently deducting expenses."}
      </p>
    </Section>
  );
}

export function SelfAssessmentPage() {
  const lookups = useLookups();
  const f = useFormat();
  const { get, set } = useUrlState();
  const years = useApi<{ current: number; years: Array<{ year: number; label: string }> }>("/api/tax/years");
  const individuals = (lookups?.owners ?? []).filter((o) => o.ownerType === "individual");
  const ownerId = get("ownerId") || individuals[0]?.id || "";
  // Self Assessment is usually filed for the tax year that has ended.
  const year = Number(get("year")) || (years.data ? years.data.current - 1 : null);
  const report = useApi<Sa105Report>(
    ownerId && year ? "/api/tax/sa105" : null,
    { ownerId, year },
    { keepPrevious: true },
  );
  // Always offer the selected year, even before it has any records.
  const yearOptions = [...(years.data?.years ?? [])];
  if (year && !yearOptions.some((y) => y.year === year))
    yearOptions.push({ year, label: taxYearLabel(year) });

  return (
    <>
      <PageHeader
        title="Self Assessment: UK property"
        subtitle="What to enter on the UK property pages (SA105) of your tax return"
        breadcrumbs={[{ label: "Tax", to: "/tax" }, { label: "Self Assessment" }]}
        actions={
          ownerId && (
            <>
              <Can permission="tax:export">
                <a
                  className="btn btn-outline-secondary"
                  href={withQuery("/api/tax/sa105/export", { ownerId, year })}
                >
                  <Icon name="download" size={16} /> Export CSV
                </a>
              </Can>
              <Button variant="outline-secondary" onClick={() => window.print()}>
                Print / PDF
              </Button>
            </>
          )
        }
      />
      <Alert variant="warning" className="disclaimer small">
        <strong>Not tax advice.</strong> {NOT_TAX_ADVICE} Check each figure before you enter it on your
        return.
      </Alert>
      {individuals.length === 0 ? (
        <EmptyState
          icon="owner"
          title="No individual owners"
          action={
            <Link className="btn btn-primary" to="/owners/new">
              Add owner
            </Link>
          }
        >
          The UK property pages are part of an individual's Self Assessment return. Add yourself as an owner
          and set your property shares.
        </EmptyState>
      ) : (
        <>
          <Card className="mb-4 no-print">
            <Card.Body className="d-flex flex-wrap gap-2 align-items-center">
              <Form.Select
                size="sm"
                style={{ width: "auto" }}
                aria-label="Owner"
                value={ownerId}
                onChange={(e) => set({ ownerId: e.target.value })}
              >
                {individuals.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Select
                size="sm"
                style={{ width: "auto" }}
                aria-label="Tax year"
                value={year ?? ""}
                onChange={(e) => set({ year: e.target.value })}
              >
                {yearOptions.map((y) => (
                  <option key={y.year} value={y.year}>
                    Tax year {y.label}
                  </option>
                ))}
              </Form.Select>
            </Card.Body>
          </Card>
          <QueryState query={report}>
            {(r) => {
              const changed = new Set(r.submission?.changedBoxes ?? []);
              return (
                <>
                  <Card className="mb-4">
                    <Card.Body>
                      <h2 className="h5 mb-1">
                        {r.owner.name}: tax year {r.label}
                      </h2>
                      <div className="small text-muted">
                        {f.date(r.range.from)} to {f.date(r.range.to)} ·{" "}
                        {r.basis === "accruals" ? "traditional accounting (accruals)" : "cash basis"} ·
                        figures as at {f.dateTime(r.generatedAt)} · return due online by{" "}
                        {f.date(r.filingDeadline)}
                      </div>
                      {r.jointlyLetProperties.length > 0 && (
                        <div className="small mt-2">
                          Jointly owned, included at {r.owner.name}'s share:{" "}
                          {r.jointlyLetProperties.map((p) => p.label).join("; ")}
                        </div>
                      )}
                      <div className="mt-3 d-flex flex-wrap gap-2 align-items-center">
                        {r.submission ? (
                          <span className="small">
                            <Icon name="check" size={14} /> Recorded as filed with HMRC on{" "}
                            {f.date(r.submission.submittedOnDate ?? r.submission.submittedAt)}
                            {r.submission.hmrcReceiptId ? ` (reference ${r.submission.hmrcReceiptId})` : ""}
                            {r.submission.submittedByName ? ` by ${r.submission.submittedByName}` : ""}.
                          </span>
                        ) : (
                          <span className="small text-muted">Not yet recorded as filed.</span>
                        )}
                        <Can permission="tax:submit">
                          <span className="no-print">
                            <ModalFormButton
                              label={r.submission ? "Record again" : "Record as filed"}
                              title={`Record your ${r.label} return as filed`}
                              schema={filedSchema}
                              defaults={{ submittedOn: "", hmrcReceiptId: "", notes: "" }}
                              onSubmit={(val) =>
                                api.post("/api/tax/mtd/submissions", {
                                  ...val,
                                  ownerId: r.owner.id,
                                  year: r.year,
                                })
                              }
                            >
                              {() => (
                                <>
                                  <p className="small">
                                    This keeps a copy of today's figures, so you'll be told if records for
                                    this year change afterwards. It doesn't send anything to HMRC.
                                  </p>
                                  <DateField name="submittedOn" label="Date you filed with HMRC" required />
                                  <TextField
                                    name="hmrcReceiptId"
                                    label="HMRC submission reference (optional)"
                                  />
                                  <TextField name="notes" label="Notes" />
                                </>
                              )}
                            </ModalFormButton>
                          </span>
                        </Can>
                      </div>
                      {r.changedSinceSubmission && (
                        <Alert variant="warning" className="small mt-3 mb-0">
                          Records changed after you filed. Boxes {r.submission?.changedBoxes.join(", ")} now
                          differ from what you recorded as filed. You may need to amend your return.
                        </Alert>
                      )}
                    </Card.Body>
                  </Card>
                  {r.warnings.map((w) => (
                    <Alert key={w} variant="info" className="small">
                      {w}
                    </Alert>
                  ))}
                  <Row>
                    <Col xl={8}>
                      <Section title="Boxes to fill in" flush>
                        <BoxTable report={r} changed={changed} />
                      </Section>
                      <p className="small text-muted no-print">
                        Select an amount to see the transactions behind it. Boxes left at £0 can be left blank
                        on the return.
                      </p>
                    </Col>
                    <Col xl={4}>
                      <Section
                        title="Figures from earlier years"
                        actions={
                          <Can permission="tax:submit">
                            <ModalFormButton
                              label="Edit"
                              title={`Earlier years and choices for ${r.label}`}
                              schema={inputsSchema}
                              defaults={{
                                lossBroughtForwardPence: r.inputs.lossBroughtForwardPence,
                                financeCostsBroughtForwardPence: r.inputs.financeCostsBroughtForwardPence,
                                claimPropertyAllowance: r.inputs.claimPropertyAllowance,
                              }}
                              onSubmit={(val) =>
                                api.put("/api/tax/sa105/inputs", {
                                  ...val,
                                  ownerId: r.owner.id,
                                  year: r.year,
                                })
                              }
                            >
                              {() => (
                                <>
                                  <MoneyField
                                    name="lossBroughtForwardPence"
                                    label="Unused losses brought forward"
                                    hint="Box 43 of last year's UK property pages."
                                  />
                                  <MoneyField
                                    name="financeCostsBroughtForwardPence"
                                    label="Unused residential finance costs brought forward"
                                    hint="Finance costs from earlier years that haven't yet reduced your tax."
                                  />
                                  <CheckboxField
                                    name="claimPropertyAllowance"
                                    label="Claim the £1,000 property income allowance instead of expenses"
                                  />
                                </>
                              )}
                            </ModalFormButton>
                          </Can>
                        }
                      >
                        <dl className="small mb-0">
                          <div className="d-flex justify-content-between">
                            <dt className="fw-normal">Losses brought forward</dt>
                            <dd className="mb-1">
                              <Money pence={r.inputs.lossBroughtForwardPence} />
                            </dd>
                          </div>
                          <div className="d-flex justify-content-between">
                            <dt className="fw-normal">Finance costs brought forward</dt>
                            <dd className="mb-1">
                              <Money pence={r.inputs.financeCostsBroughtForwardPence} />
                            </dd>
                          </div>
                          <div className="d-flex justify-content-between">
                            <dt className="fw-normal">Property income allowance</dt>
                            <dd className="mb-0">
                              {r.inputs.claimPropertyAllowance ? "claimed" : "not claimed"}
                            </dd>
                          </div>
                        </dl>
                      </Section>
                      <Section title="Working sheet (boxes 38 and 41)">
                        <DefinitionRows
                          rows={[
                            ["Income: boxes 20 + 22 + 23", r.workingSheet.incomePence],
                            ["Less: boxes 24–29, 36 and 20.1", r.workingSheet.deductionsPence],
                            ["Adjusted profit or loss", r.workingSheet.adjustedPence],
                          ]}
                        />
                      </Section>
                      <Section title="Residential finance costs (estimate)">
                        <DefinitionRows
                          rows={[
                            ["Box 44 + box 45", r.financeCostReduction.costsPence],
                            ["Relieved this year (up to box 40)", r.financeCostReduction.reliefBasePence],
                            ["Tax reduction at 20%", r.financeCostReduction.estimatedReductionPence],
                            ["Left to carry forward", r.financeCostReduction.carriedForwardPence],
                          ]}
                        />
                        <p className="small text-muted mt-2 mb-0">
                          HMRC works out the reduction on your whole return. It can be lower if your total
                          income is close to your personal allowance.
                        </p>
                      </Section>
                      <Section title="Not entered on the return">
                        <DefinitionRows
                          rows={[
                            ["Capital expenditure", r.excluded.capitalPence],
                            ["Private / non-allowable", r.excluded.nonAllowablePence],
                            ["Receipts that aren't property income", r.excluded.nonPropertyReceiptsPence],
                          ]}
                        />
                      </Section>
                    </Col>
                  </Row>
                  <Comparison r={r} />
                </>
              );
            }}
          </QueryState>
        </>
      )}
    </>
  );
}

function DefinitionRows({ rows }: { rows: Array<[string, number]> }) {
  return (
    <dl className="small mb-0">
      {rows.map(([label, pence]) => (
        <div key={label} className="d-flex justify-content-between">
          <dt className="fw-normal">{label}</dt>
          <dd className="mb-1">
            <Money pence={pence} />
          </dd>
        </div>
      ))}
    </dl>
  );
}
